import { createNonCustodialSigner } from './NonCustodialSigner';
import { createCustodialSigner } from './CustodialSigner';
import type { HorusSigner } from './types';

interface AuthInfo {
  loginOption: string;
  activePublicKey: string;
  email: string;
  indexWallet: number;
  hasPassword: boolean;
  userWalletsData?: {
    EVM?: {
      wallets: Array<{ publicKey: string }>;
    };
  } | null;
}

/**
 * Creates the appropriate XMTP signer based on the user's login type.
 *
 * - Non-custodial EVM users: uses wagmi wallet client directly
 * - Custodial users (any network): delegates to backend /signMessage using their EVM wallet
 * - Non-custodial non-EVM users: throws - they need an EVM wallet for chat
 */
export function createSigner(
  authInfo: AuthInfo,
  password?: string
): HorusSigner {
  const { loginOption, activePublicKey, email, indexWallet, userWalletsData } = authInfo;

  if (loginOption === 'non_custodial') {
    // Non-custodial: verify they have an EVM address (starts with 0x)
    if (!activePublicKey || !activePublicKey.startsWith('0x')) {
      throw new Error('Chat requires an EVM wallet. Please connect an Ethereum-compatible wallet.');
    }
    return createNonCustodialSigner(activePublicKey);
  }

  // Custodial: use EVM wallet address from userWalletsData
  const evmWallets = userWalletsData?.EVM?.wallets;
  const evmAddress = evmWallets?.[indexWallet]?.publicKey;

  if (!evmAddress) {
    throw new Error('No EVM wallet found. Please create an EVM wallet first.');
  }

  return createCustodialSigner({
    evmAddress,
    walletId: email,
    walletIndex: indexWallet,
    password,
  });
}

/**
 * Gets the EVM address that will be used as the chat identity.
 */
export function getChatAddress(authInfo: AuthInfo): string | null {
  const { loginOption, activePublicKey, userWalletsData, indexWallet } = authInfo;

  if (loginOption === 'non_custodial') {
    if (activePublicKey?.startsWith('0x')) return activePublicKey;
    return null;
  }

  // Custodial: always use EVM address
  return userWalletsData?.EVM?.wallets?.[indexWallet]?.publicKey || null;
}
